// NB: This file is in @kapitalist/contract. It is used by both @kapitalist/frontend and
// @kapitalist/backend. Changes here will require work on both sides.

import {valuableCardValue, valueOfCards} from '../../ruleset'
import type {TValuableCardData} from '../../types/cards'
import type {TMillions} from '../../types/millions'

type TImproperPaymentResponse =
  | {type: 'tooManyCards'; currentPayment: TMillions}
  // A property wild card, or a card the payer doesn't own.
  | {type: 'invalidCardSelected'}
  | {type: 'moreMoneyOwed'; currentPayment: TMillions; minimumPayment: TMillions}

type TProperPaymentResponse<Card extends TValuableCardData> = {
  type: 'proper'
  paidWith: Card[]
}

type TPaymentProprietyResponse<Card extends TValuableCardData> =
  | TProperPaymentResponse<Card>
  | TImproperPaymentResponse

/**
 * Is this selection of cards a legal way to settle a debt of `millionsOwed`?
 *
 * `valuableCards` is everything the payer _could_ pay with.
 */
export const paymentPropriety = <Card extends TValuableCardData>(
  millionsOwed: TMillions,
  valuableCards: Card[],
  selectedCardIds: ReadonlySet<string>
): TPaymentProprietyResponse<Card> => {
  const maxCapablePayment = valueOfCards(valuableCards)
  const valuableCardIds = new Set<string>(valuableCards.map(c => c.id))
  const selectedCards = valuableCards.filter(c => selectedCardIds.has(c.id))

  if ([...selectedCardIds].some(id => !valuableCardIds.has(id))) {
    return {type: 'invalidCardSelected'}
  }

  const currentPayment = valueOfCards(selectedCards)

  if (maxCapablePayment <= millionsOwed) {
    const selectedEverything = valuableCards.every(c => selectedCardIds.has(c.id))

    return selectedEverything
      ? {type: 'proper', paidWith: [...valuableCards]}
      : {type: 'moreMoneyOwed', currentPayment, minimumPayment: maxCapablePayment}
  }

  // They can cover the debt, so they have a choice to make.
  if (currentPayment < millionsOwed) {
    return {type: 'moreMoneyOwed', currentPayment, minimumPayment: millionsOwed}
  }
  if (currentPayment === millionsOwed) {
    return {type: 'proper', paidWith: selectedCards}
  }

  // Overpaying is legal only when removing any one selected card would drop the payment
  // below millionsOwed.
  for (const selectedCard of selectedCards) {
    if (currentPayment - valuableCardValue[selectedCard.type] >= millionsOwed) {
      return {type: 'tooManyCards', currentPayment}
    }
  }

  return {type: 'proper', paidWith: selectedCards}
}
